import { useLayoutEffect, useRef, useState } from "react";
import { BadgeCheck, Star } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

const Stars = ({ size = "h-4 w-4" }) => (
  <span className="flex gap-0.5" aria-label="5 out of 5 stars" role="img">
    {[0, 1, 2, 3, 4].map((i) => <Star key={i} className={size} fill="#FFC20E" stroke="#FFC20E" aria-hidden />)}
  </span>
);

const Header = ({ t, big }) => (
  <div className="flex items-start gap-3">
    <span aria-hidden className={`flex shrink-0 items-center justify-center rounded-full bg-en-teal font-display text-white ${big ? "h-14 w-14 text-xl" : "h-11 w-11 text-base"}`}>{initials(t.name)}</span>
    <div className="min-w-0 flex-1">
      <p className="flex items-center gap-1.5 font-semibold text-black">
        <span className="truncate">{t.name}</span>
        <BadgeCheck className="h-4 w-4 shrink-0 text-en-teal" aria-label="Verified client" role="img" />
      </p>
      <p className="truncate text-sm text-black/60">{t.company}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Stars />
        {t.source === "trivia_nation" && <span className="text-[11px] text-black/45">Trivia Nation client (sister company)</span>}
      </div>
    </div>
  </div>
);

export const TestimonialCard = ({ t, index }) => {
  const [open, setOpen] = useState(false);
  const [clamped, setClamped] = useState(false);
  const quoteRef = useRef(null);

  useLayoutEffect(() => {
    const el = quoteRef.current;
    const check = () => el && setClamped(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [t.quote]);

  return (
    <>
      <figure data-testid={`testimonial-card-${index}`} className="card-lift card-white flex h-[280px] flex-col p-6 sm:h-[320px]">
        <figcaption><Header t={t} /></figcaption>
        <blockquote ref={quoteRef} className="mt-4 line-clamp-5 text-[15px] leading-relaxed text-black/80">{t.quote}</blockquote>
        <button type="button" data-testid={`testimonial-see-more-${index}`} onClick={() => setOpen(true)} className={`mt-auto self-start pt-2 text-sm font-semibold text-en-teal underline-offset-4 hover:underline ${clamped ? "" : "invisible"}`} tabIndex={clamped ? 0 : -1} aria-hidden={!clamped}>See more</button>
      </figure>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`testimonial-modal-${index}`} className="max-w-lg rounded-2xl !bg-white p-7 text-black sm:p-8">
          <DialogTitle className="sr-only">Review from {t.name}</DialogTitle>
          <Header t={t} big />
          <DialogDescription asChild>
            <blockquote className="mt-5 text-base leading-relaxed text-black/80">{t.quote}</blockquote>
          </DialogDescription>
        </DialogContent>
      </Dialog>
    </>
  );
};
